/**
 * clientDatevFormat — shared, pure DATEV-Stammdaten format rules for the WL-K3
 * Kanzlei form (mirrors the server-side Zod schema in
 * `functions/src/tenant/clientDatevConfig.ts`).
 *
 * Single source of truth on the FE for the four field formats and the
 * „exportbereit?"-Ampel: all four fields set AND each in a valid format. Keeping
 * this pure (no React, no I/O) makes it trivially testable and prevents the form
 * and the indicator from drifting apart.
 *
 * @module utils/clientDatevFormat
 */

/** DATEV Berater-Nr: 1–7 digits. */
export const DATEV_BERATER_NR_RE = /^\d{1,7}$/;
/** DATEV Mandanten-Nr: 1–5 digits. */
export const DATEV_MANDANTEN_NR_RE = /^\d{1,5}$/;
/** Allowed DATEV Kontenrahmen. */
export const DATEV_SKR_VALUES = ['SKR03', 'SKR04'] as const;
/** Smallest valid Sachkontenlänge. */
export const DATEV_SACHKONTEN_LAENGE_MIN = 4;
/** Largest valid Sachkontenlänge. */
export const DATEV_SACHKONTEN_LAENGE_MAX = 8;

/** One allowed SKR value. */
export type DatevSkr = (typeof DATEV_SKR_VALUES)[number];

/** The four DATEV fields as edited/held in the form. */
export interface ClientDatevConfigView {
  datevBeraterNr: string | null;
  datevMandantenNr: string | null;
  datevSkr: DatevSkr | null;
  datevSachkontenLaenge: number | null;
}

/** True when `value` is a valid Berater-Nr (1–7 digits). */
export function isValidBeraterNr(value: string): boolean {
  return DATEV_BERATER_NR_RE.test(value.trim());
}

/** True when `value` is a valid Mandanten-Nr (1–5 digits). */
export function isValidMandantenNr(value: string): boolean {
  return DATEV_MANDANTEN_NR_RE.test(value.trim());
}

/** True when `value` is one of the allowed SKR values. */
export function isValidSkr(value: string): value is DatevSkr {
  return (DATEV_SKR_VALUES as readonly string[]).includes(value);
}

/** True when `value` is an integer within the valid Sachkontenlänge range. */
export function isValidSachkontenLaenge(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= DATEV_SACHKONTEN_LAENGE_MIN &&
    value <= DATEV_SACHKONTEN_LAENGE_MAX
  );
}

/**
 * „exportbereit?"-Ampel: green only when ALL four fields are set AND each is in a
 * valid format. Any missing or malformed field ⇒ not ready (the export would carry
 * wrong/empty DATEV master data otherwise).
 *
 * @param config - the four current field values (null = not set).
 * @returns `true` when the mandant is DATEV-export-ready.
 */
export function isExportReady(config: ClientDatevConfigView): boolean {
  const { datevBeraterNr, datevMandantenNr, datevSkr, datevSachkontenLaenge } = config;
  if (!datevBeraterNr || !isValidBeraterNr(datevBeraterNr)) return false;
  if (!datevMandantenNr || !isValidMandantenNr(datevMandantenNr)) return false;
  if (!datevSkr || !isValidSkr(datevSkr)) return false;
  if (datevSachkontenLaenge === null || !isValidSachkontenLaenge(datevSachkontenLaenge)) {
    return false;
  }
  return true;
}
